import * as React from 'react'
import * as Router from 'react-router-dom'
import { DebounceInput } from 'react-debounce-input'

import supportsWebP from 'supports-webp'

import { Draft, Post, Revision } from '../api/request'
import { createRenderer, PostImage } from '../api/content'

import { PhotoProvider, PhotoView } from 'react-photo-view'

export function PostArticleLink(props: { text: string, href: string }) {
  const { text, href } = props
  return (
    <a href={href} className="link break-all">{text}</a>
  )
}

export function PostImages(props: { isWebp: boolean | null, images: PostImage[] }) {
  const count = props.images.length
  if (props.isWebp !== null && count > 0) {
    return (
      <div className={`${props.images.length !== 1 ? `grid grid-cols-3 gap-1 md:flex md:flex-wrap ${props.images.length % 4 === 0 ? 'md:w-199' : 'md:w-150'} ` : ''}px-2 pb-2`}>
        <PhotoProvider maskOpacity={0.8}>
          {props.images.map((image: PostImage) => {
            const { id, size, webp, png, webp_url: webpUrl, png_url: pngUrl } = image
            const [width, height] = size
            return (
              <PhotoView key={id} src={props.isWebp ? webpUrl : pngUrl}>
                <img alt={props.isWebp ? webp : png} src={props.isWebp ? webpUrl : pngUrl}
                  className={`${props.images.length !== 1 || width === height ? 'w-48 aspect-square' : (width > height ? 'h-48 aspect-video' : 'w-48 aspect-video-reverse')} object-cover border-1 rounded cursor-pointer bg-gray-200`} />
              </PhotoView>
            )
          })}
        </PhotoProvider>
      </div>
    )
  }
  return (
    <div></div>
  )
}

export function PostArticle(props: { post: Post | Revision | Draft, editors: string[] }) {
  const { post, editors } = props, { id, title } = post

  const [isWebp, toggleWebp] = React.useState<boolean | null>(null)

  React.useEffect(() => (supportsWebP.then(useWebp => toggleWebp(useWebp)), undefined), [])

  const renderer = React.useMemo(() => createRenderer(PostArticleLink), [])

  const sign = editors.length > 0 ? `【本期编辑：${editors.join('，')}】` : ''

  const history = Router.useHistory()

  const titleSuffix = post.type === 'draft' ? '- 草稿' : post.type === 'revision' ? '- 已删除' : ''

  const footnoteKeys = Object.keys(post.footnotes).map(k => Number(k.match(/\[(\d+)\]/)![1])).sort((a, b) => a - b)

  return (
    <article className="space-y-2">
      <h2 className="text-2xl px-2 font-semibold">
        <a onClick={() => history.push('/' + id)}>{title}{titleSuffix}</a>
      </h2>
      {footnoteKeys.length > 0 ? (<>
        <p className="break-words px-2 pt-2 pb-0 text-justify whitespace-pre-wrap">
          <span dangerouslySetInnerHTML={{ __html: renderer.fromTextToHtml(post.text, post, post).innerHTML }} />
          <span>{sign}</span>
        </p>
        <p className="break-words px-2 pt-0 pb-2 text-justify whitespace-pre-wrap">{footnoteKeys.flatMap(id => {
          const key = `[${id}]`, text = post.footnotes[key]
          const url = new URL(post['footnote_urls'][key] ?? '#', location.href)
          const full = url.origin !== location.origin || url.pathname !== location.pathname
          const href = full ? url.href : url.search + url.hash
          const name = new URL(href, location.href).hash.substring(1)
          return [
            <span id={full ? undefined : name}>{id === 1 ? '[' : '\n['}
              <PostArticleLink text={'' + id} href={href} />]:
            </span>,
            <span dangerouslySetInnerHTML={{ __html: renderer.fromTextToHtml(text, post, post).innerHTML }} />
          ]
        })}</p>
      </>) : (<p className="break-words p-2 text-justify whitespace-pre-wrap">
        <span dangerouslySetInnerHTML={{ __html: renderer.fromTextToHtml(post.text, post, post).innerHTML }} />
        <span>{sign}</span>
      </p>)}
      <PostImages isWebp={isWebp} images={post.images} />
    </article>
  )
}

export function PostLoading() {
  return (
    <article className="animate-pulse space-y-2 pb-4">
      <h2 className="rounded-full mx-2 h-8 bg-gray-200 w-54 mb-5"></h2>
      <p className="rounded-full mx-2 h-4 bg-gray-200"></p>
      <p className="rounded-full mx-2 h-4 bg-gray-200"></p>
      <p className="rounded-full mx-2 h-4 bg-gray-200"></p>
      <p className="rounded-full mx-2 h-4 bg-gray-200 w-1/3"></p>
    </article>
  )
}

export function PostNotFound() {
  return (
    <article className="space-y-2">
      <h2 className="text-2xl px-2 font-semibold">#??（????????????????）</h2>
      <p className="text-justify p-2 break-words">啊呀。你想找的这期茶后谈，好像并不存在呢。</p>
    </article>
  )
}

export function PostSearchBar(props: { onSubmit(value: string): void, onLoading(): void }) {
  return (
    <div className="px-1.75 mb-4 transform -translate-y-2">
      <DebounceInput
        id="q" type="search"
        debounceTimeout={300}
        placeholder="检索已有的茶后谈"
        onChangeCapture={() => props.onLoading()}
        onChange={(input) => props.onSubmit(input.target.value)}
        className="border-t-0 border-r-0 border-b-2 border-l-0 border-gray-400 shadow-none
                 bg-transparent w-full px-0.5 py-2 px-0 focus:outline-none focus:border-gray-500" />
    </div>
  )
}
